// src/hooks/useUiSetting.js
// 画面の表示設定を人ごとに覚える【2026-09-17】
//
//   サーバー（/api/ui-settings、v42）に保存するので、別のパソコンで開いても同じ表示になる。
//   サーバーが使えない（v42 未適用・通信できない）ときはブラウザにだけ覚える。
//   変更はすぐ画面に反映し、サーバーへの保存は少し待ってまとめて送る。
import { useCallback, useEffect, useRef, useState } from 'react';

const token = () => { try { return localStorage.getItem('token') || ''; } catch (e) { return ''; } };

let cache = null;        // { token, stored, settings }
let pending = null;

function fetchAll() {
  const t = token();
  if (cache && cache.token === t) return Promise.resolve(cache);
  if (pending && pending.token === t) return pending.promise;
  const promise = fetch('/api/ui-settings', { headers: { Authorization: 'Bearer ' + t } })
    .then(r => r.json())
    .then(d => ({ token: t, stored: !!(d && d.success && d.stored), settings: (d && d.settings) || {} }))
    .catch(() => ({ token: t, stored: false, settings: {} }))
    .then(c => { cache = c; pending = null; return c; });
  pending = { token: t, promise };
  return promise;
}

const LOCAL = (key) => 'ui:' + key;
function readLocal(key, legacy) {
  try {
    const v = JSON.parse(localStorage.getItem(LOCAL(key)) || 'null');
    if (v && typeof v === 'object') return v;
  } catch (e) { /* 読めなければ無し */ }
  return legacy ? legacy() : null;
}
function writeLocal(key, v) {
  try { localStorage.setItem(LOCAL(key), JSON.stringify(v)); } catch (e) { /* 覚えられなくても動く */ }
}

/** テスト用: 覚えている分を捨てる */
export function _resetUiSettingCache() { cache = null; pending = null; }

/**
 * @param {string} key       設定の名前（サーバー側で許可したもの）
 * @param {object} defaults  既定値
 * @param {function} normalize 読み込んだ値を整える (value) => value
 * @param {function} [legacy] 以前の保存場所から読む () => object|null
 * @returns {[object, function, boolean|null]} [値, 変更（部分でよい）, サーバーに保存できるか]
 */
export default function useUiSetting(key, defaults, normalize = (v) => v, legacy) {
  const [value, setValue] = useState(() => normalize({ ...defaults, ...(readLocal(key, legacy) || {}) }));
  const [stored, setStored] = useState(null);
  const ref = useRef(value);
  const timer = useRef(null);

  useEffect(() => {
    let alive = true;
    fetchAll().then(c => {
      if (!alive) return;
      setStored(c.stored);
      const sv = c.settings && c.settings[key];
      if (sv && typeof sv === 'object') {
        const next = normalize({ ...defaults, ...sv });
        ref.current = next;
        setValue(next);
        writeLocal(key, next);
      }
    });
    return () => { alive = false; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => clearTimeout(timer.current), []);

  const update = useCallback((patch) => {
    const cur = ref.current;
    const next = normalize({ ...cur, ...(typeof patch === 'function' ? patch(cur) : patch) });
    ref.current = next;
    setValue(next);
    writeLocal(key, next);
    if (cache) cache = { ...cache, settings: { ...cache.settings, [key]: next } };
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      fetch('/api/ui-settings/' + encodeURIComponent(key), {
        method: 'PUT',
        headers: { Authorization: 'Bearer ' + token(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: next }),
      }).then(r => setStored(r.ok)).catch(() => setStored(false));
    }, 400);
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  return [value, update, stored];
}
